import JSZip from "jszip";
import { AIReviewEvaluationGroup, AIReviewEvaluation, AIReview } from "common/models/types";
import { fetchAnalysisResult } from "api/aiReviewService";

/**
 * 파일명에서 사용할 수 없는 문자를 치환
 */
function sanitizeFileName(name: string): string {
  return name.replace(/[\/\\:*?"<>|]/g, "-");
}

/**
 * 같은 patientId에 대해 회차(iteration)를 부여
 */
function assignIterations(evaluations: AIReviewEvaluation[]): Map<number, number> {
  const iterationMap = new Map<number, number>();
  const patientGroups = new Map<number, AIReviewEvaluation[]>();

  for (const evaluation of evaluations) {
    const pid = evaluation.patientId;
    if (!patientGroups.has(pid)) {
      patientGroups.set(pid, []);
    }
    patientGroups.get(pid)!.push(evaluation);
  }

  patientGroups.forEach((group) => {
    group.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
    group.forEach((evaluation, index) => {
      iterationMap.set(evaluation.id!, index + 1);
    });
  });

  return iterationMap;
}

/**
 * 평가 데이터에서 .md 파일 내용을 생성
 */
function generateMarkdownContent(
  evaluation: AIReviewEvaluation,
  aiReview: AIReview | null | undefined,
  group: AIReviewEvaluationGroup
): string {
  const model = aiReview?.analysis?.model || aiReview?.request?.modelType || "-";
  const promptType = aiReview?.request?.promptType || aiReview?.promptType || "-";
  const promptId = aiReview?.request?.promptId ?? "-";
  const evaluationModel = evaluation.evaluationModel || group.evaluationModel || "-";
  const score = evaluation.score ?? "-";
  const evaluatedAt = evaluation.evaluatedAt
    ? new Date(evaluation.evaluatedAt).toLocaleString("ko-KR")
    : "-";

  const reviewResult = aiReview?.reviewResult || "*보고서 데이터 없음*";
  const evaluationComments = evaluation.evaluationComments || "*평가 코멘트 없음*";
  const groundTruthSnapshot = evaluation.groundTruthSnapshot || "*Ground Truth 없음*";
  const reasoningLog = aiReview?.reasoningLog || "*Reasoning Log 없음*";

  return `# AI Review 평가 보고서

| 항목 | 값 |
|------|-----|
| 환자번호 | ${evaluation.patientId} |
| 보고서 AI 모델 | ${model} |
| 프롬프트 타입 | ${promptType} |
| 프롬프트 ID | ${promptId} |
| 평가 AI 모델 | ${evaluationModel} |
| 평가 점수 | ${score} |
| 평가 시점 | ${evaluatedAt} |
| AI Review ID | ${evaluation.aiReviewId} |

---

## 보고서 (Review Result)

${reviewResult}

---

## 평가 결과 (Evaluation)

### 점수: ${score}

### 평가 코멘트
${evaluationComments}

### Ground Truth
${groundTruthSnapshot}

---

## Reasoning Log

${reasoningLog}
`;
}

/**
 * 동시 요청 제한 유틸
 */
async function fetchWithConcurrencyLimit<T>(
  tasks: (() => Promise<T>)[],
  limit: number,
  onProgress?: (completed: number, total: number) => void
): Promise<T[]> {
  const results: T[] = [];
  let index = 0;
  let completed = 0;

  async function runNext(): Promise<void> {
    const currentIndex = index++;
    if (currentIndex >= tasks.length) return;
    results[currentIndex] = await tasks[currentIndex]();
    completed++;
    onProgress?.(completed, tasks.length);
    return runNext();
  }

  const workers = Array(Math.min(limit, tasks.length))
    .fill(null)
    .map(() => runNext());
  await Promise.all(workers);
  return results;
}

/**
 * aiReview가 evaluation에 포함되지 않은 경우 개별 fetch
 */
async function fetchMissingAIReviews(
  evaluations: AIReviewEvaluation[],
  onProgress?: (completed: number, total: number) => void
): Promise<Map<number, AIReview | null>> {
  const reviewMap = new Map<number, AIReview | null>();
  const tasks = evaluations.map((evaluation) => async () => {
    try {
      const response = await fetchAnalysisResult(String(evaluation.aiReviewId));
      if (response.error === 0 && response.data) {
        reviewMap.set(evaluation.aiReviewId, response.data);
      } else {
        reviewMap.set(evaluation.aiReviewId, null);
      }
    } catch {
      reviewMap.set(evaluation.aiReviewId, null);
    }
  });

  await fetchWithConcurrencyLimit(tasks, 5, onProgress);
  return reviewMap;
}

/**
 * 폴더명 생성
 */
function buildFolderName(group: AIReviewEvaluationGroup, firstAiReview: AIReview | null | undefined): string {
  const promptType = firstAiReview?.request?.promptType || firstAiReview?.promptType || "unknown";
  const model = firstAiReview?.analysis?.model || firstAiReview?.request?.modelType || "unknown";
  const promptId = firstAiReview?.request?.promptId ?? "0";
  const evaluationModel = group.evaluationModel || "unknown";
  const avgScore = group.averageScore != null ? Math.round(group.averageScore) : 0;

  return sanitizeFileName(`${promptType}_${model}_${promptId}_${evaluationModel}_avg${avgScore}`);
}

/**
 * 파일명 생성
 */
function buildFileName(
  evaluation: AIReviewEvaluation,
  aiReview: AIReview | null | undefined,
  group: AIReviewEvaluationGroup,
  iteration: number
): string {
  const promptType = aiReview?.request?.promptType || aiReview?.promptType || "unknown";
  const model = aiReview?.analysis?.model || aiReview?.request?.modelType || "unknown";
  const promptId = aiReview?.request?.promptId ?? "0";
  const evaluationModel = evaluation.evaluationModel || group.evaluationModel || "unknown";
  const patientId = evaluation.patientId || 0;
  const score = evaluation.score != null ? Math.round(evaluation.score) : 0;

  return sanitizeFileName(
    `${promptType}_${model}_${promptId}_${evaluationModel}_${patientId}_${iteration}_${score}.md`
  );
}

/**
 * 평가 그룹 데이터를 ZIP으로 다운로드
 */
export async function downloadEvaluationGroupAsZip(
  group: AIReviewEvaluationGroup,
  evaluations: AIReviewEvaluation[],
  onProgress?: (message: string) => void
): Promise<void> {
  if (evaluations.length === 0) {
    throw new Error("다운로드할 평가 데이터가 없습니다.");
  }

  // aiReview 관계 데이터 확인 - 첫 번째 evaluation에 aiReview가 없으면 개별 fetch
  const hasAiReviewData = evaluations[0]?.aiReview != null;
  let aiReviewMap: Map<number, AIReview | null> | null = null;

  if (!hasAiReviewData) {
    onProgress?.("AI Review 데이터를 가져오는 중...");
    aiReviewMap = await fetchMissingAIReviews(evaluations, (completed, total) => {
      onProgress?.(`AI Review 데이터를 가져오는 중... (${completed}/${total})`);
    });
  }

  onProgress?.("파일 생성 중...");

  const getAiReview = (evaluation: AIReviewEvaluation): AIReview | null | undefined => {
    if (hasAiReviewData) return evaluation.aiReview;
    return aiReviewMap?.get(evaluation.aiReviewId) ?? null;
  };

  // 회차 부여
  const iterationMap = assignIterations(evaluations);

  // 폴더명 생성 (첫 번째 evaluation의 aiReview 기준)
  const firstAiReview = getAiReview(evaluations[0]);
  const folderName = buildFolderName(group, firstAiReview);

  // ZIP 생성
  const zip = new JSZip();
  const folder = zip.folder(folderName)!;

  for (const evaluation of evaluations) {
    const aiReview = getAiReview(evaluation);
    const iteration = iterationMap.get(evaluation.id!) || 1;
    const fileName = buildFileName(evaluation, aiReview, group, iteration);
    const content = generateMarkdownContent(evaluation, aiReview, group);
    folder.file(fileName, content);
  }

  onProgress?.("ZIP 파일 생성 중...");
  const blob = await zip.generateAsync({ type: "blob" });

  // 다운로드
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${folderName}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
